// Emergency mode (PMU-273): the school's emergency as this device knows it (its own records and
// those heard on the Bluetooth mesh), and the mesh's life: standby when offline (a door always),
// full while an incident is open, off otherwise. The staff app and the door start it; screens read
// the state (useEmergency). Records also go into the outbox, so online they reach the cloud as usual.
import { useSyncExternalStore } from "react";
import {
  DeviceApi, EmergencyMesh, emergencyPre, flush, onEnqueue, readMeshKeys, readRoster, readPeers, refreshMeshKeys, refreshPeers,
  type DeviceRecord, type MeshEvent, type VavrDb,
} from "@vavr/client";
import type { PreImage } from "@vavr/core";
import type { MeshBridge } from "./platform.ts";

export interface RollCallMark { status: string; place: string | null; note: string | null; at: number; by: string }
export interface ClassReport { accounted: number; missing: string[]; complete: boolean; at: number; by: string }
export interface Release { at: number; by: string; adultId: string | null }
/** A message or a priority alert (E4); `to` is a member of staff's id (null: everyone). */
export interface MeshMessage { id: string; kind: "message" | "alert"; text: string | null; alert: string | null; place: string | null; to: string | null; at: number; by: string; device: string }
export interface Incident {
  id: string; kind: string; drill: boolean; place: string | null; note: string | null;
  declaredAt: number; declaredBy: string; endedAt: number | null;
  rollCall: Record<string, RollCallMark>; reports: Record<string, ClassReport>;
  /** Children handed to an approved adult during the incident (reunification releases). */
  released: Record<string, Release>;
  /** Messages and alerts, newest first. */
  messages: MeshMessage[];
}
export interface EmergencyState { incident: Incident | null; mesh: "off" | "standby" | "full"; records: number }

let state: EmergencyState = { incident: null, mesh: "off", records: 0 };
const incidents = new Map<string, Incident>();
const subs = new Set<() => void>();
const emit = () => { const open = [...incidents.values()].filter((i) => i.endedAt === null).sort((a, b) => b.declaredAt - a.declaredAt)[0];
  const latest = open ?? [...incidents.values()].sort((a, b) => b.declaredAt - a.declaredAt)[0] ?? null;
  state = { ...state, incident: latest ? { ...latest } : null }; for (const f of subs) f(); };
const setMesh = (m: EmergencyState["mesh"]) => { if (state.mesh !== m) { state = { ...state, mesh: m }; for (const f of subs) f(); } };

export const emergencyState = () => state;
const messageSubs = new Set<(m: MeshMessage, i: Incident) => void>();
/** Every new message or alert as it arrives (once each), for an in-app alert. */
export function onEmergencyMessage(f: (m: MeshMessage, i: Incident) => void) { messageSubs.add(f); return () => { messageSubs.delete(f); }; }
export function useEmergency(): EmergencyState { return useSyncExternalStore((f) => { subs.add(f); return () => { subs.delete(f); }; }, emergencyState, emergencyState); }

/** Applies one record to the state (each once; the newest mark per child or class wins). */
export function applyEmergencyRecord(pre: PreImage) {
  const d = (pre.data ?? {}) as Record<string, unknown>;
  // A reunification release (the core pickup, flagged): on the open incident.
  if (pre.type === "pickup" && Array.isArray(pre.flags) && pre.flags.includes("reunification") && pre.child_id) {
    const open = [...incidents.values()].filter((i) => i.endedAt === null || i.endedAt >= pre.device_ts).sort((a, b) => b.declaredAt - a.declaredAt)[0];
    if (!open) return;
    open.released = { ...open.released, [pre.child_id]: { at: pre.device_ts, by: pre.staff_id ?? pre.device_id, adultId: pre.adult_id ?? null } };
    state = { ...state, records: state.records + 1 };
    emit();
    return;
  }
  const id = typeof d.incident === "string" ? d.incident : null;
  if (!id || typeof pre.type !== "string" || !pre.type.startsWith("emergency:")) return;
  const by = pre.staff_id ?? pre.device_id;
  let inc = incidents.get(id);
  if (pre.type === "emergency:declare") {
    if (!inc) inc = { id, kind: String(d.kind), drill: d.drill === true, place: (d.place as string) ?? null, note: (d.note as string) ?? null, declaredAt: pre.device_ts, declaredBy: by, endedAt: null, rollCall: {}, reports: {}, released: {}, messages: [] };
    else Object.assign(inc, { kind: String(d.kind), drill: d.drill === true, place: (d.place as string) ?? null, note: (d.note as string) ?? null, declaredAt: pre.device_ts, declaredBy: by });
    incidents.set(id, inc);
  } else {
    // A record can arrive before its declaration (sync order): keep it on a placeholder.
    if (!inc) { inc = { id, kind: "unknown", drill: false, place: null, note: null, declaredAt: pre.device_ts, declaredBy: by, endedAt: null, rollCall: {}, reports: {}, released: {}, messages: [] }; incidents.set(id, inc); }
    if (pre.type === "emergency:end") inc.endedAt = Math.max(inc.endedAt ?? 0, pre.device_ts);
    if (pre.type === "emergency:roll_call" && pre.child_id) {
      const old = inc.rollCall[pre.child_id];
      if (!old || old.at <= pre.device_ts) inc.rollCall = { ...inc.rollCall, [pre.child_id]: { status: String(d.status), place: (d.place as string) ?? null, note: (d.note as string) ?? null, at: pre.device_ts, by } };
    }
    if ((pre.type === "emergency:message" || pre.type === "emergency:alert") && !inc.messages.some((m) => m.id === pre.id)) {
      const m: MeshMessage = { id: pre.id, kind: pre.type === "emergency:alert" ? "alert" : "message", text: (d.text as string) ?? null, alert: (d.alert as string) ?? null, place: (d.place as string) ?? null, to: (d.to as string) ?? null, at: pre.device_ts, by, device: pre.device_id };
      inc.messages = [m, ...inc.messages].sort((a, b) => b.at - a.at).slice(0, 500);
      for (const f of messageSubs) f(m, inc);
    }
    if (pre.type === "emergency:class_report") {
      const room = (d.room_id as string) ?? "";
      const old = inc.reports[room];
      if (!old || old.at <= pre.device_ts) inc.reports = { ...inc.reports, [room]: { accounted: Number(d.accounted), missing: typeof d.missing === "string" && d.missing ? d.missing.split(",") : [], complete: d.complete === true, at: pre.device_ts, by } };
    }
  }
  state = { ...state, records: state.records + 1 };
  emit();
}

/** A reunification declared on the mesh (open): the staff screens treat it like the office's. */
export function meshReunification(i: Incident | null): { since: number; place: string | null } | null {
  return i && i.endedAt === null && i.kind === "reunification" ? { since: i.declaredAt, place: i.place } : null;
}

/** The server's view of the school's incident (GET /sites/:site/emergency, the door's request poll). */
export interface ServerIncident {
  incident: { id: string; kind: string; drill: boolean; place: string | null; note: string | null; declared_at: number; declared_by: string; ended_at: number | null; ended_by: string | null } | null;
  roll_call?: { child_id: string; status: string; place: string | null; note: string | null; at: number; by: string }[];
  reports?: { room_id: string; accounted: number; missing: string[]; complete: boolean; at: number; by: string }[];
  messages?: { id: string; kind: string; text: string | null; alert: string | null; place: string | null; to: string | null; at: number; by: string }[];
  /** The last day's incidents and any still open: their declarations and ends. */
  recent?: NonNullable<ServerIncident["incident"]>[];
}

/** Applies what the server holds, as the records it was built from (each mark keeps its own time). */
let lastServer = "";
export function applyServerIncident(cur: ServerIncident | null) {
  if (!cur) return;
  const key = JSON.stringify(cur);
  if (key === lastServer) return; // the door asks every 5 s: nothing new, nothing to do
  lastServer = key;
  const pre = (type: string, at: number, by: string, data: Record<string, unknown>, child: string | null = null) =>
    ({ v: 2, id: `srv_${type}_${String(data.incident)}_${child ?? ""}`, type, site_id: "", device_id: by, staff_id: by, child_id: child, device_ts: at, flags: [], data }) as unknown as PreImage;
  // Earlier incidents first: an end this device missed closes the incident here too.
  for (const r of [...(cur.recent ?? [])].reverse()) {
    if (!incidents.has(r.id) && r.ended_at != null) continue; // over before this device heard of it
    applyEmergencyRecord(pre("emergency:declare", r.declared_at, r.declared_by, { incident: r.id, kind: r.kind, drill: r.drill, place: r.place, note: r.note }));
    if (r.ended_at != null) applyEmergencyRecord(pre("emergency:end", r.ended_at, r.ended_by ?? "", { incident: r.id }));
  }
  const i = cur.incident;
  if (!i) return;
  applyEmergencyRecord(pre("emergency:declare", i.declared_at, i.declared_by, { incident: i.id, kind: i.kind, drill: i.drill, place: i.place, note: i.note }));
  for (const m of cur!.roll_call ?? []) applyEmergencyRecord(pre("emergency:roll_call", m.at, m.by, { incident: i.id, status: m.status, place: m.place, note: m.note }, m.child_id));
  for (const m of [...(cur.messages ?? [])].reverse())
    applyEmergencyRecord({ ...pre(m.kind === "alert" ? "emergency:alert" : "emergency:message", m.at, m.by, { incident: i.id, text: m.text, alert: m.alert, place: m.place, to: m.to }), id: m.id });
  for (const r of cur!.reports ?? []) applyEmergencyRecord(pre("emergency:class_report", r.at, r.by, { incident: i.id, room_id: r.room_id, accounted: r.accounted, missing: r.missing.join(","), complete: r.complete }));
  if (i.ended_at != null) applyEmergencyRecord(pre("emergency:end", i.ended_at, i.ended_by ?? "", { incident: i.id }));
}

/** A live socket message: an emergency record (or a reunification release) applies at once. */
export function applyLiveEmergency(m: unknown) {
  const msg = m as { type?: string; event?: { pre?: PreImage } } | null;
  const ev = msg?.type === "event" ? msg.event?.pre : null;
  if (ev && typeof ev.type === "string" && (ev.type.startsWith("emergency:") || ev.type === "pickup")) applyEmergencyRecord(ev);
}

let active: EmergencyController | null = null;
/** This device's emergency controller (the staff app's or the door's), for the screens. */
export const emergencyController = () => active;

export interface EmergencyController {
  /** Records an emergency event on this device and sends it over the mesh. */
  record(type: `emergency:${string}`, childId: string | null, data: Record<string, string | number | boolean | null>): Promise<MeshEvent>;
  stop(): void;
  /** Reads the server's incident again (the live socket reconnected: it may have missed records). */
  resync(): void;
  /** The mesh's own view: its mode and links (how many devices this one reaches directly). */
  meshStatus(): Promise<{ mode: string; links: number } | null>;
}

/**
 * Starts this device's part: keys and the school's devices (while online), the state from what the
 * device holds, the mesh's mode as the situation changes, and uploads of carried records.
 */
export function startEmergency(o: {
  db: VavrDb; device: DeviceRecord; staffId: string; mesh?: MeshBridge; router: boolean; online: () => boolean; api: DeviceApi;
  /** The server's current incident (while online): at start, and whenever the app comes back online. */
  current?: () => Promise<ServerIncident | null>;
  /** Subscribes to the app going offline or online; returns the unsubscribe. */
  onlineChanges?: (f: () => void) => () => void;
}): EmergencyController {
  const { db, device } = o;
  let stopped = false, mode: EmergencyState["mesh"] = "off", starting = false, synced = false;
  const em = new EmergencyMesh({
    db, deviceId: device.device_id, privateKey: device.keys.privateKey, mirrorKey: device.mirrorKey,
    mesh: { send: async (p) => { if (o.mesh && mode !== "off") await o.mesh.send(p); } },
    onRecord: (ev) => applyEmergencyRecord(ev.pre),
  });
  const refresh = async () => {
    if (!o.online()) return;
    await refreshPeers(db, o.api).catch(() => undefined);
    if (await refreshMeshKeys(db, o.api, device.mirrorKey).then(() => true, () => false) && mode !== "off") await pushInfo();
    if (o.current && !synced) { const cur = await o.current().catch(() => null); if (cur) { applyServerIncident(cur); synced = true; } }
    await em.uploadCarried(o.api).catch(() => undefined);
  };
  // The service words its own notifications while the app is away: it needs the day keys and names.
  const pushInfo = async () => {
    if (!o.mesh?.setInfo) return;
    const [keys, roster] = await Promise.all([readMeshKeys(db, device.mirrorKey), readRoster(db, device.mirrorKey).catch(() => null)]);
    const names: Record<string, string> = {};
    for (const st of roster?.staff ?? []) names[st.id] = st.name;
    await o.mesh.setInfo({ keys, staffId: o.staffId, names }).catch(() => undefined);
  };
  // The mesh's mode: full while an incident is open, standby when offline (a door always), else off.
  const decide = async () => {
    if (stopped || !o.mesh || starting) return;
    const open = state.incident && state.incident.endedAt === null;
    const want: EmergencyState["mesh"] = open ? "full" : (o.router || !o.online()) ? "standby" : "off";
    if (want === mode) return;
    starting = true;
    try {
      if (want === "off") await o.mesh.stop();
      else if (mode === "off") {
        const peers = (await readPeers(db)).map((p) => ({ id: p.id, kind: p.kind, pubkey: p.pubkey }));
        await o.mesh.start({ self: device.device_id, siteId: device.site_id!, router: o.router, mode: want, peers });
        await pushInfo();
        for (const p of await o.mesh.drain()) await em.received(p);
      } else await o.mesh.setMode(want);
      console.info("emergency mesh:", mode, "->", want);
      mode = want; setMesh(want);
    } catch (e) { console.warn("mesh", e); }
    finally { starting = false; }
  };
  const off = o.mesh?.onPacket((p) => { void em.received(p).then(() => decide()); });
  // During an incident a reunification release made on this device also goes over the mesh, so
  // every device sees who has gone home and nobody releases a child twice.
  const unEnqueue = onEnqueue((e) => {
    if (e.pre.type !== "pickup" || !e.pre.flags?.includes("reunification")) return;
    applyEmergencyRecord(e.pre);
    void em.share({ pre: e.pre, device_sig: e.device_sig });
  });
  void (async () => {
    // The state from what this device holds: its own emergency records and those heard on the mesh.
    const own = await db.outbox.toArray();
    for (const e of own) if (typeof e.pre.type === "string" && e.pre.type.startsWith("emergency:")) applyEmergencyRecord(e.pre);
    for (const ev of await em.heard()) applyEmergencyRecord(ev.pre);
    for (const e of own) if (e.pre.type === "pickup") applyEmergencyRecord(e.pre);
    // The native mesh outlives the app's page (its service keeps relaying): pick up its mode.
    const running = await o.mesh?.status().catch(() => null);
    if (running && (running.mode === "standby" || running.mode === "full")) { mode = running.mode; setMesh(mode); await pushInfo(); }
    await refresh();
    await decide();
  })();
  const tick = setInterval(() => { void refresh().then(decide); }, 15_000);
  // Going offline or back online changes the mesh at once (standby needs to be listening before a
  // declaration is made, not 15 s after).
  const offOnline = o.onlineChanges?.(() => { void decide(); if (o.online()) { synced = false; void refresh(); } });
  const ctl: EmergencyController = {
    record: async (type, childId, data) => {
      const pre = emergencyPre({ siteId: device.site_id!, deviceId: device.device_id, staffId: o.staffId, monoStart: typeof performance === "undefined" ? Date.now() : performance.timeOrigin }, type, childId, data);
      // A declaration starts the mesh in full before it is sent, so it goes out at once.
      if (type === "emergency:declare") applyEmergencyRecord(pre);
      await decide();
      const drill = type === "emergency:declare" ? data.drill === true : state.incident?.drill === true;
      const ev = await em.record(pre, { drill });
      // Online, it goes to the server now, not at the outbox's next round (every phone hears it from
      // there). A flush already running may have missed it: a second one follows.
      if (o.online()) void flush(db, o.api).catch(() => undefined).then(() => flush(db, o.api)).catch(() => undefined);
      return ev;
    },
    resync: () => { synced = false; void refresh(); },
    meshStatus: async () => {
      if (!o.mesh || mode === "off") return null;
      const st = await o.mesh.status().catch(() => null);
      return st ? { mode: String(st.mode), links: Number(st.links ?? 0) } : null;
    },
    stop: () => { stopped = true; if (active === ctl) active = null; unEnqueue(); clearInterval(tick); off?.(); offOnline?.(); if (mode !== "off") void o.mesh?.stop(); mode = "off"; setMesh("off"); },
  };
  active = ctl;
  // A test hook for the device tests.
  if (typeof window !== "undefined") (window as unknown as { vavrEmergency?: unknown }).vavrEmergency = { ...ctl, state: emergencyState, decide };
  return ctl;
}
